import { useState, useEffect, useCallback } from 'react';
import EmailConfirmation from './EmailConfirmation';

function CheckRow({ check }) {
  return (
    <li className="flex items-start gap-1.5">
      <span className={`mt-px flex-shrink-0 ${check.ok ? 'text-emerald-600' : 'text-amber-600'}`}>
        {check.ok ? '✓' : '!'}
      </span>
      <span className="text-[11px] text-gray-600">
        <span className="font-medium text-gray-700">{check.name}</span>
        {check.detail ? ` — ${check.detail}` : ''}
      </span>
    </li>
  );
}

/**
 * Closes out a processing run without leaving the modal: verify what actually
 * landed in the database, then send the confirmation email right here. Before
 * this, the email lived behind the dashboard's missing-email banner, so a run
 * wasn't really finished when the modal said it was.
 *
 * Verification is per batch (orders in production, authorized, addressed, QR
 * art ready). Failing checks don't hard-block the send — art generation lags a
 * minute or two on reorders, and only the operator knows when an exception is
 * fine — but they do require ticking "send anyway" first.
 *
 * Each check first asks the server to move the batch's authorized orders into
 * production. Greenlight/MSP/backbook applies already did that themselves;
 * for orders the import tool created (reorders, new stands) it's the step
 * that can only happen once the tool has finished their QR art, so it runs
 * here — and again on every re-check until the art is in.
 */
export default function BatchCompletion({ batches, batchDate, secretKey, onNeedAuth, onAuthFailed, onSent }) {
  const [results, setResults] = useState(null);
  const [checking, setChecking] = useState(true);
  const [override, setOverride] = useState(false);

  // Join to a primitive so the effect doesn't re-run on every parent render
  const labelKey = batches.map((b) => b.label).join('|');

  const runChecks = useCallback(async () => {
    setChecking(true);
    const labels = labelKey.split('|');
    const rs = await Promise.all(
      labels.map(async (label) => {
        try {
          if (secretKey) {
            // Best-effort: the verify below reports whatever state results
            await fetch(`/api/greenlight/promote/${encodeURIComponent(label)}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ secret: secretKey }),
            }).catch(() => {});
          }
          const res = await fetch(`/api/greenlight/verify/${encodeURIComponent(label)}`);
          const d = await res.json();
          if (!res.ok) throw new Error(d.error || 'Verification failed');
          return d;
        } catch (err) {
          return { label, ok: false, checks: [{ name: 'Verification', ok: false, detail: err.message }] };
        }
      })
    );
    setResults(rs);
    setChecking(false);
  }, [labelKey, secretKey]);

  useEffect(() => {
    runChecks();
  }, [runChecks]);

  const allOk = results !== null && results.every((r) => r.ok);
  const issueCount = results ? results.reduce((n, r) => n + r.checks.filter((c) => !c.ok).length, 0) : 0;

  return (
    <div className="mt-5 w-full max-w-md">
      <div className="border border-gray-200 rounded-xl px-4 py-3.5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-xs font-semibold text-gray-900">
            {checking ? 'Checking the data…' : allOk ? 'Everything checks out' : `${issueCount} thing${issueCount !== 1 ? 's' : ''} to look at`}
          </p>
          <button
            onClick={runChecks}
            disabled={checking}
            className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-40 flex-shrink-0"
          >
            {checking ? 'Checking…' : 'Re-check'}
          </button>
        </div>

        {results && (
          <div className="space-y-2.5">
            {results.map((r) => (
              <div key={r.label}>
                <p className="text-[11px] font-mono font-semibold text-gray-700 mb-1">
                  {r.label}
                  {r.orderCount != null && (
                    <span className="font-sans font-normal text-gray-400">
                      {' '}· {r.orderCount} order{r.orderCount !== 1 ? 's' : ''}
                      {r.codeCount > 0 ? ` · ${r.codeCount} codes` : ''}
                    </span>
                  )}
                </p>
                <ul className="space-y-0.5">
                  {r.checks.map((c) => (
                    <CheckRow key={c.name} check={c} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {results && !allOk && (
          <label className="flex items-start gap-2 mt-3 pt-2.5 border-t border-gray-100 cursor-pointer">
            <input
              type="checkbox"
              checked={override}
              onChange={(e) => setOverride(e.target.checked)}
              className="mt-0.5 flex-shrink-0"
            />
            <span className="text-[11px] text-amber-800">
              Send the email anyway — I've reviewed the flagged items above.
            </span>
          </label>
        )}
      </div>

      <EmailConfirmation
        batches={batches}
        batchDate={batchDate}
        secretKey={secretKey}
        onNeedAuth={onNeedAuth}
        onAuthFailed={onAuthFailed}
        onSent={onSent}
        disabled={checking || !results || (!allOk && !override)}
        disabledReason={
          checking || !results
            ? 'Running the checks…'
            : 'Resolve the flagged items above, or tick "send anyway" to override.'
        }
      />
    </div>
  );
}
